import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePaginatedQuery } from '@/components/common/use-paginated-query';

import { ApiClientError } from '@/lib/api';
import {
    createPublishJob,
    getPublishJob,
    listOrganizationPublishingLimits,
    listPublishingTargets,
    listPublishJobs,
    type CreatePublishJobInput,
    type PublishingLimitFeatureKey
} from '@/features/publishing/services/publishing';

export const publishingKeys = {
    all: ['publishing'] as const,
    organizations: () => [...publishingKeys.all, 'organizations'] as const,
    organization: (organizationId: string) => [...publishingKeys.organizations(), organizationId] as const,
    jobs: (organizationId: string) => [...publishingKeys.organization(organizationId), 'jobs'] as const,
    jobList: (organizationId: string, page: number, perPage: number) =>
        [...publishingKeys.jobs(organizationId), 'list', page, perPage] as const,
    jobDetail: (organizationId: string, publishJobId: string) =>
        [...publishingKeys.jobs(organizationId), 'detail', publishJobId] as const,
    targets: (organizationId: string) => [...publishingKeys.organization(organizationId), 'targets'] as const,
    limits: (organizationId: string) => [...publishingKeys.organization(organizationId), 'limits'] as const,
    limitList: (organizationId: string, featureKey?: PublishingLimitFeatureKey) =>
        [...publishingKeys.limits(organizationId), 'list', featureKey ?? 'all'] as const
};

export function usePublishJobs(args: { organizationId: string | null; page?: number; perPage?: number }) {
    const page = args.page ?? 1;
    const perPage = args.perPage ?? 50;

    return usePaginatedQuery({
        queryKey: args.organizationId ? publishingKeys.jobList(args.organizationId, page, perPage) : publishingKeys.all,
        queryFn: () => listPublishJobs({ organizationId: args.organizationId!, page, perPage }),
        enabled: Boolean(args.organizationId)
    });
}

export function usePublishJob(organizationId: string | null, publishJobId: string | null) {
    return useQuery({
        queryKey:
            organizationId && publishJobId
                ? publishingKeys.jobDetail(organizationId, publishJobId)
                : publishingKeys.all,
        queryFn: () => getPublishJob({ organizationId: organizationId!, publishJobId: publishJobId! }),
        enabled: Boolean(organizationId && publishJobId)
    });
}

export function usePublishingTargets(organizationId: string | null) {
    return useQuery({
        queryKey: organizationId ? publishingKeys.targets(organizationId) : publishingKeys.all,
        queryFn: () => listPublishingTargets(organizationId!),
        enabled: Boolean(organizationId)
    });
}

export function useOrganizationPublishingLimits(organizationId: string | null, featureKey?: PublishingLimitFeatureKey) {
    return useQuery({
        queryKey: organizationId ? publishingKeys.limitList(organizationId, featureKey) : publishingKeys.all,
        queryFn: () => listOrganizationPublishingLimits({ organizationId: organizationId!, featureKey }),
        enabled: Boolean(organizationId)
    });
}

export function useCreatePublishJob(organizationId: string | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: CreatePublishJobInput) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return createPublishJob({ organizationId, input });
        },
        onSuccess: async () => {
            if (!organizationId) {
                return;
            }

            await queryClient.invalidateQueries({ queryKey: publishingKeys.jobs(organizationId) });
        }
    });
}
